import { create } from 'zustand';
import { groupsService } from 'src/services/groups';
import type { GroupResponse, GroupPayload } from 'src/services/groups';

interface GroupsState {
  items: GroupResponse[];
  current: GroupResponse | null;
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (v: string) => void;
  loadList: () => Promise<void>;
  loadOne: (id: string | number) => Promise<void>;
  create: (payload: GroupPayload) => Promise<GroupResponse>;
  update: (id: string | number, payload: GroupPayload) => Promise<GroupResponse>;
  remove: (id: string | number) => Promise<void>;
  uploadImage: (id: string | number, file: File, imageName: 'logo' | 'welcome' | 'emailHeader' | 'emailFooter') => Promise<GroupResponse>;
}

export const useGroupsStore = create<GroupsState>((set, get) => ({
  items: [],
  current: null,
  loading: false,
  error: null,
  search: '',
  setSearch: (v: string) => set({ search: v }),
  loadList: async () => {
    set({ loading: true, error: null });
    try {
      const { search } = get();
      const response = await groupsService.getList(search);
      set({ items: response.data });
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to load groups' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  loadOne: async (id) => {
    set({ loading: true, error: null });
    try {
      const data = await groupsService.getOne(id);
      set({ current: data });
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to load group' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  create: async (payload) => {
    set({ loading: true, error: null });
    try {
      const created = await groupsService.create(payload);
      set({ items: [created, ...get().items] });
      return created as GroupResponse;
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to create group' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  update: async (id, payload) => {
    set({ loading: true, error: null });
    try {
      const updated = await groupsService.update(id, payload);
      set({
        current: get().current && (get().current as GroupResponse).id === updated.id ? updated : get().current,
        items: get().items.map((g) => (g.id === updated.id ? updated : g)),
      });
      return updated as GroupResponse;
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to update group' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  remove: async (id) => {
    set({ loading: true, error: null });
    try {
      await groupsService.remove(id);
      set({
        items: get().items.filter((g) => g.id !== id),
        current: get().current && (get().current as GroupResponse).id === id ? null : get().current,
      });
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to delete group' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  uploadImage: async (id, file, imageName) => {
    set({ loading: true, error: null });
    try {
      const updated = await groupsService.uploadImage(id, file, imageName);
      set({
        current: get().current && (get().current as GroupResponse).id === updated.id ? updated : get().current,
        items: get().items.map((g) => (g.id === updated.id ? updated : g)),
      });
      return updated;
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to upload image' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
}));


